const THREE = require('three');
const OrbitControls = require('three-orbit-controls')(THREE);

import {generateBoundaries, generateBoundary} from './boundaries';

import Axis from './axis';

const START_CAMERA_POSITION = {
  free: {x: 0, y: 0, z: -400},
  blank: {x: 0, y: -400, z: 0},
  nest: {x: 0, y: -400, z: 0}
}

const DISTANCE_RATIO = 400;

const DEFAULT_LIGHT_POSITION = {x: 1000, y: 1000, z: 1000};
const DEFAULT_LIGHT_COLOR = 0xffffff;
const CLEAR_COLOR = 0x888888;

const DEFAULT_MATERIAL_COLOR = 0x3556b7;
const GREEN_MATERIAL_COLOR = 0x00ff00;
const RED_MATERIAL_COLOR = 0xff0000;

const ARROW_CYR_MATERIAL_COLOR = 0xffff00;
const BOUNDARY_COLOR = 0x49D149;

class BaseScene {

    constructor(width, height, maxDimensions, position, viewType) {
        this.viewType = viewType;
        this.cameraDistanceRatio = maxDimensions/ DISTANCE_RATIO;
        this.width = width;
        this.height = height;
        this.ratio = width/height;
        this.initScene();
        this.initCamera(width, height, position);
        this.initRenderer(width, height);
        this.initControls(this.camera)
    }

    restoreView(width, height, positionObj) {
        const position  = positionObj[this.viewType];
        this.initCamera(width, height, position);
        this.renderer.setSize(width, height);

        this.width = width;
        this.height = height;
        this.ratio = width/height;

        this.camera.aspect = 1;
        this.initControls(this.camera);
    }

    initSpotLight(position = DEFAULT_LIGHT_POSITION, color = DEFAULT_LIGHT_COLOR) {
        let {x, y, z} = position;
        let spotLight = new THREE.SpotLight(color);
        spotLight.position.set(x, y, z);
        spotLight.castShadow = true;
        return spotLight;
    }

    initScene() {
        let scene = new THREE.Scene();
        this.light = this.initSpotLight();
        scene.add(this.light);
        this.scene = scene;
    }

    setLightPosition(camera) {
      this.light.position.set(camera.position.x, camera.position.y, camera.position.z);
    }

    initRenderer(width, height) {
        let renderer = new THREE.WebGLRenderer({
          preserveDrawingBuffer: true,
          antialias: true,
          alpha: true
        });

        this.renderer = renderer;
        this.renderer.setSize(width, height);
        renderer.setClearColor(CLEAR_COLOR, 0.0);
        renderer.autoClear = true;
        renderer.shadowMapEnabled = true;
    }

    generateGeometry(rawGeometry, Y) {
        let statistic = {};
        let max = (key, value, statistic) => {
          const prevValue = statistic[key];
          return prevValue === undefined || prevValue < value ? value: prevValue;
        };

        let min = (key, value, statistic) => {
            const prevValue = statistic[key];
            return prevValue === undefined || prevValue > value ? value: prevValue;
        };

        let processCoordinate = (key, value) => {
            const maxKey = `max${key}`;
            const minKey = `min${key}`;
            statistic[maxKey] = max(maxKey, value, statistic);
            statistic[minKey] = min(minKey, value, statistic);
        };

        let geometry = new THREE.Geometry();

        rawGeometry.vertices.forEach((point) => {
            const [x, y, z] = point;
            processCoordinate ('X', x);
            processCoordinate ('Z', z);
            processCoordinate ('Y', y);
            if(Y) {
                geometry.vertices.push(new THREE.Vector3(x, Y, z));
            } else {
                geometry.vertices.push(new THREE.Vector3(x, y, z));
            }

        });

        rawGeometry.facets.forEach((face) => {
            let [x, y, z] = face;
                geometry.faces.push(new THREE.Face3(x, y, z));
        });
        geometry.computeFaceNormals();
        geometry.mergeVertices();
        geometry.computeVertexNormals();
        geometry.castShadow = true;
        geometry.hasColors = true;
        return geometry;
    }

    initAxis() {
        this.axis = new Axis(this.scene, this.ratio);
        this.scene.add(this.axis);
    }

    getAxis() {
        return this.axis;
    }

    initControls(camera) {
        this.controls = new OrbitControls(camera, this.renderer.domElement);
        this.controls.noKeys = true;
    }

    setDimensions(width, height) {
        this.width = width;
        this.height = height;
    }

    getCamera(width, height) {
        return new THREE.PerspectiveCamera(45, width / height, 5, 20000);
    }

    setCameraUp() {
        this.camera.up.set( 1, 0, 0 );
    }

    initCamera(width, height, position) {
        let camera = this.getCamera(width, height);
        this.camera = camera;
        this.setCameraUp();
        camera.rotation.x = 90 * Math.PI / 180;

        const camPosition = START_CAMERA_POSITION[this.viewType];
        if (position) {
            camera.position.x = position.x;
            camera.position.y = position.y;
            camera.position.z = position.z;
        } else {
            camera.position.x = camPosition.x * this.cameraDistanceRatio;
            camera.position.y = camPosition.y * this.cameraDistanceRatio;
            camera.position.z = camPosition.z * this.cameraDistanceRatio;
        }
    }

    getControls() {
        return this.controls;
    }

    limitCameraMovements() {
        let controls = this.getControls();
        controls.minPolarAngle = -Math.PI;
        controls.maxPolarAngle = -Math.PI;
        controls.maxDistance = this.cameraDistanceRatio * this.getMaxDistanceRatio();
    }

    getBase64Image() {
        const strMime = "image/png";
        return this.renderer.domElement.toDataURL(strMime, 1);
    }

    initArrow(type, maxDimentions) {
        const DIMENSION_RATIO = maxDimentions / 250;
        const colorMapper = {
            '0': DEFAULT_MATERIAL_COLOR,
            '1': GREEN_MATERIAL_COLOR,
            '-1': RED_MATERIAL_COLOR,
        };
        const CYL_RAD = 6 * DIMENSION_RATIO;
        const CYL_WIDTH = 2 * DIMENSION_RATIO;
        const ARR_RAD = 7 * DIMENSION_RATIO;
        const ARR_LEN = 5 * DIMENSION_RATIO;
        const TOTAL_LEN = 20 * DIMENSION_RATIO;
        const HAF_TOTAL_LEN = 10 * DIMENSION_RATIO;
        const CIR_RAD = 3 * DIMENSION_RATIO;
        const CIR_WIDTH = 1 * DIMENSION_RATIO;

        const material = new THREE.MeshPhongMaterial({color: colorMapper[type]});

        const plate1 = new THREE.CylinderGeometry(CYL_RAD, CYL_RAD, CYL_WIDTH);
        const plate1Mesh = new THREE.Mesh(plate1, material);
        plate1Mesh.position.set(0, -HAF_TOTAL_LEN, 0);

        const plate2 = new THREE.CylinderGeometry(CYL_RAD, CYL_RAD, CYL_WIDTH);
        const plate2Mesh = new THREE.Mesh(plate2, material);
        plate2Mesh.position.set(0, HAF_TOTAL_LEN, 0);


        const arrow1 = new THREE.CylinderGeometry(0, ARR_LEN, ARR_RAD);
        const arrow1Mesh = new THREE.Mesh(arrow1, material);
        arrow1Mesh.position.set(0, HAF_TOTAL_LEN, 0);

        const arrow2 = new THREE.CylinderGeometry(ARR_LEN, 0, ARR_RAD);
        const arrow2Mesh = new THREE.Mesh(arrow2, material);
        arrow2Mesh.position.set(0, -HAF_TOTAL_LEN, 0);


        const settingsMapper = {
            '0': {
                edge1: plate1Mesh,
                edge2: plate2Mesh
            }, '1': {
                edge1: arrow1Mesh,
                edge2: plate1Mesh
            }, '-1': {
                edge1: arrow2Mesh,
                edge2: plate2Mesh
            }
        };

        const container = new THREE.Object3D();

        const cirMaterial = new THREE.MeshPhongMaterial({color: ARROW_CYR_MATERIAL_COLOR});

        const body = new THREE.CylinderGeometry(CYL_WIDTH, CYL_WIDTH, TOTAL_LEN);
        const bodyMesh = new THREE.Mesh(body, material);
        container.add(bodyMesh);

        const cir = new THREE.CylinderGeometry(CIR_RAD, CIR_RAD, CIR_WIDTH);
        const cirMesh = new THREE.Mesh(cir, cirMaterial);
        container.add(cirMesh);

        container.add(settingsMapper[type].edge1);
        container.add(settingsMapper[type].edge2);

        container.rotateX(Math.PI/2);

        this.scene.add(container);
    }
}


class MeshScene extends BaseScene {

    constructor (rawGeometry, width, height, position) {

        super(width, height, rawGeometry.maxDimensions, position.free, 'free');
        this.data = rawGeometry;
        const geometry = this.generateGeometry(rawGeometry.mesh);
        const material = new THREE.MeshPhongMaterial({ opacity: geometry.alpha, vertexColors: THREE.VertexColors,  side: THREE.DoubleSide, color: DEFAULT_MATERIAL_COLOR});
        material.side = THREE.DoubleSide;
        let mesh = new THREE.Mesh(geometry, material);
        this.scene.add(mesh);

        this.initArrow(rawGeometry.offset.value, rawGeometry.maxDimensions);
        this.initAxis();
        this.getControls().center = geometry.center();
    }
}


class FormabilityScene extends BaseScene {

  constructor (data, width, height, position) {
      const [geometry, maxDimensions] = data;
      super( width, height, maxDimensions, position.free, 'free');
      geometry.mergeVertices();
      geometry.computeVertexNormals();
      const material = new THREE.MeshPhongMaterial({ opacity: 1, vertexColors: THREE.VertexColors,  side: THREE.DoubleSide});
      material.side = THREE.DoubleSide;
      let mesh = new THREE.Mesh(geometry, material);
      this.scene.add(mesh);
      this.initAxis();

      this.getControls().center = geometry.center();
  }
}


class BlankScene extends BaseScene {

    setCameraUp() {
        this.camera.up.set( 0, -1, 0);
    }

    getMaxDistanceRatio() {
        return 900;
    }

    constructor (data, width, height, position) {
        const [boundaryData, meshData, maxDimensions] = data;
        super(width, height, maxDimensions, position.blank, 'blank');

        const boundaryObj = generateBoundary(boundaryData);
        let mesh = this.generateGeometry(meshData, boundaryObj[0].maxY + 1 );
        let [, boundary] = boundaryObj;
        const lineMaterial = new THREE.LineBasicMaterial( {linewidth: 1} );
        lineMaterial.color.setHex(BOUNDARY_COLOR);
        const line = new THREE.Line(boundary, lineMaterial);
        const meshMaterial = new THREE.MeshPhongMaterial({opacity: mesh.alpha, vertexColors: THREE.VertexColors,  side: THREE.DoubleSide, color: DEFAULT_MATERIAL_COLOR});
        meshMaterial.side = THREE.DoubleSide;
        let meshLL = new THREE.Mesh(mesh, meshMaterial);

        this.scene.add(line);
        this.scene.add(meshLL);

        this.limitCameraMovements();
    }

    restoreView(width, height, position) {
        super.restoreView(width, height, position);
        this.limitCameraMovements();
    }
}

class NestScene extends BaseScene {

    constructor (data, width, height, position) {

        const [boundariesData, edges, maxDimensions] = data;
        super( width, height, maxDimensions * 4, position.nest, 'nest');
        const lineMaterial = new THREE.LineBasicMaterial({linewidth: 20});
        lineMaterial.color.setHex(BOUNDARY_COLOR);

        let [boundaryData, boundaries] = generateBoundaries(boundariesData, edges, maxDimensions);
        this.container = new THREE.Object3D();

        boundaries.forEach((boundaryObj) => {
            const [, boundary] = boundaryObj;
            const line = new THREE.Line(boundary, lineMaterial);
            this.container.add(line);
        });
        let [, plane] = this.getPlane(boundaryData, edges);
        this.maxPlateDimention = _.max([boundaryData.dimentionX, boundaryData.dimentionZ]) * 1.5;
        this.container.add(plane);
        this.container.rotation.y = Math.PI / 2;
        this.scene.add(this.container);



        this.limitCameraMovements();
    }

    limitCameraMovements() {
        let controls = this.getControls();
        controls.minPolarAngle = -Math.PI;
        controls.maxPolarAngle = -Math.PI;
        controls.maxDistance = this.maxPlateDimention;
    }

    getMaxDistanceRatio() {
        return 550;
    }

    setLightPosition(camera) {
        this.light.position.set(camera.position.x, camera.position.y - 400, camera.position.z);
    }

    setCameraUp() {
        this.camera.up.set(0, -1, 0);
    }

    restoreView(width, height, position) {
        super.restoreView(width, height, position);
        this.limitCameraMovements();
    }

    getPlane(boundaryData, edges) {
        let planeGeometry = new THREE.PlaneGeometry(boundaryData.dimentionX + edges.edge*2, boundaryData.dimentionZ);
        let planeMaterial = new THREE.MeshLambertMaterial({color: DEFAULT_MATERIAL_COLOR, side: THREE.DoubleSide});
        let plane = new THREE.Mesh(planeGeometry, planeMaterial);

        plane.rotation.set(Math.PI/2, 0, 0);
        plane.position.y = 1;
        plane.position.x = boundaryData.middleX;
        plane.position.z = boundaryData.middleZ;

        plane.receiveShadow = true;
        return [planeGeometry, plane];
    }
}



export {MeshScene, FormabilityScene, BlankScene, NestScene};



// WEBPACK FOOTER //
// ./client/app/common/model-view/scenes.js